'use client';

import { useCallback, useEffect, useState } from 'react';
import { Capabilities } from '@kit/capabilities';
import { useCapabilityOr } from '@kit/capabilities/client';
import { createPortal } from 'react-dom';

import Link from '~/(tenant)/_components/tenant-link';
import { usePathname, useRouter } from 'next/navigation';

import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Calendar,
  CalendarPlus,
  Dumbbell,
  Globe,
  Home,
  LogIn,
  LogOut,
  type LucideIcon,
  MapPin,
  Menu,
  Package,
  Shield,
  Star,
  Trophy,
  User,
  Users,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  clearBiometricCredentials,
  useBiometricTokenSync,
} from '@kit/auth/native';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Trans } from '@kit/ui/trans';
import { useIsMobile } from '@kit/ui/use-mobile';

import { BodyBrandingStyle } from '~/components/body-branding-style';
import { TopBarLocationSwitcher } from '~/(tenant)/_components/native/top-bar-location-switcher';
import type { VenueOption } from '~/(tenant)/_components/venue-picker/use-venue-picker';
import { WebVenuePicker } from '~/(tenant)/_components/venue-picker/web-venue-picker';
import type { PublicLocationRow } from '~/(tenant)/book/_lib/server/booking-page.loader';
import { useTenantBranding } from '~/(tenant)/_components/tenant-branding-provider';
import { useIsNative } from '~/(tenant)/_lib/capacitor/capacitor-context';
import { I18N_COOKIE_NAME } from '~/lib/i18n/i18n.settings';

import { NativeLayoutContext } from './_lib/native-layout-context';
import { useNativePresentation } from './_lib/use-native-presentation';
import { SAFE_AREA_INSET, useSystemBarRail } from './_lib/use-system-bar-rail';
import presentationStyles from './native-presentation.module.css';
import { getNativeTabRoutes } from './_lib/native-tab-routes';
import { attachNativeHomeSwipe } from './_lib/native-home-swipe';
import type { TabsConfig } from '../../_lib/schema/native-home-config.schema';
import { useNativeNavigationScope, useNativeBack, useNativeCanGoBack, useNativeOverlayContainer } from './native-navigation';

// Icon lookup for dynamic menu items from config
const ICON_MAP: Record<string, LucideIcon> = {
  'calendar-plus': CalendarPlus,
  calendar: Calendar,
  package: Package,
  'map-pin': MapPin,
  user: User,
  dumbbell: Dumbbell,
  trophy: Trophy,
  users: Users,
  star: Star,
  home: Home,
};

// Fixed menu items that always appear
const FIXED_MENU_ITEMS = [
  {
    labelKey: 'common:bookNow',
    defaultLabel: 'Book Now',
    icon: CalendarPlus,
    path: '/book',
  },
  {
    labelKey: 'common:bookings',
    defaultLabel: 'My Bookings',
    icon: Calendar,
    path: '/bookings',
  },
  {
    labelKey: 'common:packages',
    defaultLabel: 'Packages',
    icon: Package,
    path: '/packages',
  },
  {
    labelKey: 'coaches:title',
    defaultLabel: 'Coaches',
    icon: Dumbbell,
    path: '/coaches',
  },
  {
    labelKey: 'common:routes.profile',
    defaultLabel: 'Profile',
    icon: User,
    path: '/profile',
  },
] as const;

interface QuickActionConfig {
  label: string;
  icon: string;
  href: string;
}

/**
 * Wraps the tenant layout. When the mobile app experience is active
 * (Capacitor native or mobile web with ios_app_enabled), replaces the
 * web header/footer with a native-style header (burger menu + drawer)
 * and bottom tab bar. On desktop, passes through header/footer unchanged.
 *
 * The members-only gate is enforced in middleware (see `gateMembersOnlyTenant`
 * in apps/web/proxy.ts). A client overlay used to live here too but was
 * removed: children mounted underneath and fired their loaders, leaking
 * class images, booking RPCs, and RSC fetches through the gate.
 */
export function NativeLayoutShell({
  header,
  footer,
  children,
  enabled,
  tabsConfig,
  tenantAccountId,
  quickActions,
  locations,
  venueFunnel,
  promptVenueOnLanding,
  appBackgroundColor,
  appTextColor,
}: {
  header: React.ReactNode;
  footer: React.ReactNode;
  children: React.ReactNode;
  enabled: boolean;
  tabsConfig?: TabsConfig;
  /** Tenant account UUID — used for the top-bar location switcher and drawer admin link */
  tenantAccountId?: string;
  /** Quick actions from the home screen config — added as extra drawer menu items */
  quickActions?: QuickActionConfig[];
  /** Bookable locations for the tenant. Drives the native top-bar venue
   *  switcher and the web venue picker; both hide below two venues. */
  locations?: VenueOption[];
  /** Rich location rows for school networks, so both venue pickers use the
   *  /book Region → City → Club funnel. Absent for everyone else. */
  venueFunnel?: PublicLocationRow[];
  /** Website builder opt-in: open the web venue picker on first visit to landing pages too. */
  promptVenueOnLanding?: boolean;
  /** App page background — drives header, drawer, loader, sign-out overlay. */
  appBackgroundColor?: string;
  /** App text/ink color — drives icons, titles, spinner stroke. */
  appTextColor?: string;
}) {
  const { isNative, isReady } = useIsNative();
  const isMobile = useIsMobile();
  const branding = useTenantBranding();
  useBiometricTokenSync();
  const hasPackages = useCapabilityOr(Capabilities.PACKAGES, true);
  const tabRoutes = getNativeTabRoutes(tabsConfig, hasPackages);
  useNativeNavigationScope(tenantAccountId);

  const showNativeShell = enabled && isReady && (isNative || isMobile);
  const bodyBranding = (
    <BodyBrandingStyle
      primaryColor={branding.primaryColor}
      secondaryColor={branding.secondaryColor}
      appBackgroundColor={showNativeShell ? appBackgroundColor : undefined}
      appTextColor={showNativeShell ? appTextColor : undefined}
    />
  );

  // Show a branded full-screen loader until native detection is ready,
  // then reveal everything at once — no header/content flash.
  if (enabled && !isReady) {
    const bg = appBackgroundColor || '#FFFFFF';
    const fg = appTextColor || '#1C1C1C';

    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center"
        style={{ backgroundColor: bg }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke={fg}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="animate-spin"
        >
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      </div>
    );
  }

  if (!showNativeShell) {
    const page = (
      <>
        {bodyBranding}
        {header}
        {children}
        {footer}
      </>
    );

    // While `enabled` detection is pending the loader above is shown, so the
    // web picker never mounts (and auto-opens) inside a native app.
    return tenantAccountId && locations && locations.length >= 2 ? (
      <WebVenuePicker
        accountId={tenantAccountId}
        locations={locations}
        funnelLocations={venueFunnel}
        promptOnLanding={promptVenueOnLanding}
      >
        {page}
      </WebVenuePicker>
    ) : (
      page
    );
  }

  return (
    <NativeLayoutContext.Provider value={true}>
      {bodyBranding}
      <NativeThemeFrame>
        <NativeHeader
          tabRoutes={tabRoutes}
          quickActions={quickActions}
          tenantAccountId={tenantAccountId}
          locations={locations}
          venueFunnel={venueFunnel}
        />
        <NativeMain>{children}</NativeMain>
      </NativeThemeFrame>
    </NativeLayoutContext.Provider>
  );

}

function NativeMain({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const rail = useSystemBarRail();
  const isMembership =
    pathname === '/membership' || pathname.startsWith('/membership/');

  return (
    <main
      className="flex flex-1 flex-col"
      style={{
        // Clear the system bar column on iPhone Duo and landscape cutouts.
        paddingLeft: SAFE_AREA_INSET.left,
        paddingRight: SAFE_AREA_INSET.right,
        paddingBottom: isMembership
          ? 0
          : rail
            ? `calc(${SAFE_AREA_INSET.bottom} + 16px)`
            : `calc(80px + ${SAFE_AREA_INSET.bottom})`,
      }}
    >
      {children}
    </main>
  );
}

function NativeThemeFrame({ children }: { children: React.ReactNode }) {
  const presentation = useNativePresentation();
  return (
    <div
      style={presentation.style}
      className={`flex min-h-screen flex-col ${presentationStyles.foundation}`}
    >
      {children}
    </div>
  );
}

// (The old client-side useMembershipGate hook was removed — the gate now
// lives in middleware so RSC payloads and prefetches can never bypass it.)

// Top-level routes that correspond to bottom tab bar items.
// Any pathname that does NOT exactly match one of these is considered a "sub-page"
// that should show a back button pointing at its parent tab.
// "/" is treated separately: it matches only the exact root, never as a prefix.
const MAIN_TAB_ROUTES = [
  '/',
  '/book',
  '/bookings',
  '/packages',
  '/profile',
] as const;

function NativeHeader({
  tabRoutes,
  quickActions,
  tenantAccountId,
  locations,
  venueFunnel,
}: {
  tabRoutes: string[];
  quickActions?: QuickActionConfig[];
  tenantAccountId?: string;
  locations?: VenueOption[];
  venueFunnel?: PublicLocationRow[];
}) {
  const pathname = usePathname();
  const router = useRouter();
  const { isNative } = useIsNative();
  const nativeBack = useNativeBack();
  const canGoBack = useNativeCanGoBack();
  const branding = useTenantBranding();
  const { t, i18n } = useTranslation();
  const rail = useSystemBarRail();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const rtl = i18n.dir() === 'rtl';

  // Home has no back destination; its edge gesture opens the existing menu.
  useEffect(() => {
    if (!isNative || pathname !== '/' || canGoBack) return;
    return attachNativeHomeSwipe(openDrawer, rtl);
  }, [isNative, pathname, canGoBack, openDrawer, rtl]);

  const isMainRoute = tabRoutes.some((route) =>
    route === '/' ? pathname === '/' : pathname === route,
  );

  // Derive the logical parent tab from the current pathname (native iOS-style back navigation).
  //
  // We deliberately avoid router.back() because browser history may contain external
  // pages from payment 3DS flows (Moyasar, Tap, HyperPay). Pressing back after a
  // successful 3DS redirect would send the user back into the payment gateway UI.
  //
  // Instead, we walk the MAIN_TAB_ROUTES list and find the longest prefix match,
  // which is always the correct tab root for the current page.
  // Example: /bookings/123 → /bookings, /book/confirm → /book, /profile/edit → /profile
  // Falls back to "/" only when no tab prefix matches (e.g. /coaches sub-pages).
  const getParentRoute = useCallback((): string => {
    // Find the most-specific tab route whose prefix matches the current pathname.
    // "/" is excluded — it would match every path as a prefix.
    //
    // Sort by descending length before matching so a longer prefix always wins
    // over a shorter one. Without this, "/book" would incorrectly match
    // "/bookings/123" before "/bookings" gets a chance to match.
    const matchingTab = [...MAIN_TAB_ROUTES]
      .filter((route) => route !== '/')
      .sort((a, b) => b.length - a.length)
      .find((route) => pathname.startsWith(route));
    return matchingTab ?? '/';
  }, [pathname]);

  // Native shell colors come from app_background_color / app_text_color
  // (NOT primary/secondary, which are reserved for the website).
  const headerBg = branding.appBackgroundColor || '#FFFFFF';
  const headerFg = branding.appTextColor || '#1C1C1C';
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  return (
    <>
      <header
        className="fixed top-0 z-40 px-2 pb-1"
        style={{
          backgroundColor: headerBg,
          paddingTop: `calc(${SAFE_AREA_INSET.top} + 4px)`,
          // Stop at the side insets so the iPhone Duo system bar column stays one clean strip.
          left: SAFE_AREA_INSET.left,
          right: SAFE_AREA_INSET.right,
          borderBottom: `1px solid ${headerFg}14`,
          // iOS 26 WKWebView workaround (WebKit #297779) — pin to visual
          // viewport via compositor layer so the header doesn't drift on
          // scroll-direction changes.
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
          willChange: 'transform',
          contain: 'layout',
        }}
      >
        <div className="relative flex h-11 items-center justify-between gap-2">
          {/* Left group: hamburger/back + logo. min-w-0 lets the logo shrink
              when the right-side venue chip needs space. */}
          <div className="flex min-w-0 flex-1 items-center gap-1">
            {isMainRoute && !canGoBack ? (
              <button
                onClick={openDrawer}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors active:bg-white/10"
                aria-label={t('common:memberClub.nav.openMenuAriaLabel')}
              >
                <Menu className="h-5 w-5" style={{ color: headerFg }} />
              </button>
            ) : (
              <button
                onClick={() => isNative ? nativeBack() : router.push(getParentRoute())}
                data-test="native-back"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors active:bg-white/10"
                aria-label={t('common:goBack')}
              >
                <ArrowLeft className="h-5 w-5 rtl:rotate-180" style={{ color: headerFg }} />
              </button>
            )}

            {branding.logoUrl ? (
              <img
                dir="ltr"
                src={branding.logoUrl}
                alt={branding.accountName ?? ''}
                className="-ms-2 h-7 w-auto max-w-[140px] shrink object-contain"
              />
            ) : (
              <span
                dir="ltr"
                className="-ms-2 truncate text-xl font-bold capitalize"
                style={{ color: headerFg }}
              >
                {branding.accountName}
              </span>
            )}
          </div>

          {/* Right side: venue switcher for multi-location tenants. shrink-0
              so it never gets squeezed by a wide logo. Self-hides for
              single-location tenants. */}
          {tenantAccountId && locations && locations.length >= 2 && (
            <div className="shrink-0 pe-1">
              <TopBarLocationSwitcher
                accountId={tenantAccountId}
                locations={locations}
                funnelLocations={venueFunnel}
              />
            </div>
          )}
        </div>
      </header>

      <div style={{ height: `calc(${SAFE_AREA_INSET.top} + 48px)` }} />

      <NativeDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        hasTabRail={rail !== null}
        branding={branding}
        quickActions={quickActions}
        tenantAccountId={tenantAccountId}
      />
    </>
  );
}

function NativeDrawer({
  open,
  onClose,
  hasTabRail,
  branding,
  quickActions,
  tenantAccountId,
}: {
  open: boolean;
  onClose: () => void;
  hasTabRail: boolean;
  branding: ReturnType<typeof useTenantBranding>;
  quickActions?: QuickActionConfig[];
  tenantAccountId?: string;
}) {
  const pathname = usePathname();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const client = useSupabase();
  const overlayContainer = useNativeOverlayContainer();
  // Shares the ['session'] key (and its in-flight fetch) with the home-screen
  // sections and the tab bar — a private 'drawer-session' key meant one extra
  // auth.getSession() per boot and missed the sign-out invalidation in
  // site-navigation.tsx.
  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
    staleTime: 60_000,
  });
  const isAuthenticated = !!session?.user;
  const userId = session?.user?.id;

  const { data: hasTenantRole } = useQuery({
    queryKey: ['drawer-tenant-role', userId, tenantAccountId],
    queryFn: async () => {
      const { data } = await client
        .from('accounts_memberships')
        .select('user_id')
        .eq('user_id', userId!)
        .eq('account_id', tenantAccountId!)
        .limit(1)
        .maybeSingle();
      return !!data;
    },
    enabled: !!userId && !!tenantAccountId,
  });

  const adminPanelHref = branding.accountSlug
    ? `/home/${branding.accountSlug}`
    : '/home';

  const signOut = useSignOut({
    onSuccess: () => {
      window.location.href = '/';
    },
  });
  // Drawer surfaces use app_background_color / app_text_color (NOT brand
  // primary/secondary, which are website-only).
  const drawerBg = branding.appBackgroundColor || '#FFFFFF';
  const drawerFg = branding.appTextColor || '#1C1C1C';
  // Subtle ink-on-bg overlay for active/pressed nav states. Mirrors how
  // Frame Studio uses ${foreground}10–14 for low-contrast wash effects.
  const activeWash = `${drawerFg}1F`; // ~12% ink
  const pressedWash = `${drawerFg}14`; // ~8% ink
  const dividerWash = `${drawerFg}1A`; // ~10% ink

  // Show a DOM-level loader that persists across page navigation
  const showSignOutLoader = useCallback(() => {
    const overlay = document.createElement('div');
    overlay.id = 'sign-out-overlay';
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '9999',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: drawerBg,
    });
    overlay.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="${drawerFg}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation:spin 1s linear infinite"><path d="M21 12a9 9 0 1 1-6.219-8.56"/><style>@keyframes spin{to{transform:rotate(360deg)}}</style></svg>`;
    document.body.appendChild(overlay);
  }, [drawerBg, drawerFg]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    document.cookie = `${I18N_COOKIE_NAME}=${newLang};path=/;max-age=31536000`;
    window.location.reload();
  };

  // Build dynamic menu items from quick actions config (deduplicated against fixed items).
  // Match by path prefix so variants like `/book`, `/book/`, or `/book?x=y` from a
  // tenant's home-config quick action don't appear as a redundant drawer row next to
  // the fixed "Book Now" entry.
  const fixedPaths = FIXED_MENU_ITEMS.map((item) => item.path);
  const isFixedHref = (href: string) => {
    const path = href.split('?')[0]?.replace(/\/$/, '') ?? href;
    return fixedPaths.some((p) => path === p || path.startsWith(`${p}/`));
  };
  const dynamicItems = (quickActions ?? [])
    .filter((action) => !isFixedHref(action.href))
    .map((action) => ({
      label: action.label,
      icon: ICON_MAP[action.icon] ?? Star,
      path: action.href,
    }));

  const drawer = (
    <>
      <div
        data-native-menu-open={open ? 'true' : 'false'}
        className={`fixed inset-0 z-[60] bg-black/50 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
      />

      <div
        className={`fixed inset-y-0 z-[60] flex w-72 flex-col transition-transform duration-300 ease-out ${
          isRtl ? 'right-0' : 'left-0'
        } ${
          open
            ? 'translate-x-0'
            : isRtl
              ? 'translate-x-full'
              : '-translate-x-full'
        }`}
        style={{
          backgroundColor: drawerBg,
          paddingTop: SAFE_AREA_INSET.top,
          [isRtl ? 'paddingRight' : 'paddingLeft']: isRtl
            ? SAFE_AREA_INSET.right
            : SAFE_AREA_INSET.left,
        }}
      >
        {/* Drawer header */}
        <div className="flex h-14 items-center justify-between px-4">
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.accountName ?? ''}
              className="h-8 w-auto object-contain"
            />
          ) : (
            <span
              className="text-xl font-bold capitalize"
              style={{ color: drawerFg }}
            >
              {branding.accountName}
            </span>
          )}

          <button
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-full transition-colors"
            style={{ color: drawerFg }}
            onPointerDown={(e) =>
              (e.currentTarget.style.backgroundColor = pressedWash)
            }
            onPointerUp={(e) =>
              (e.currentTarget.style.backgroundColor = 'transparent')
            }
            onPointerLeave={(e) =>
              (e.currentTarget.style.backgroundColor = 'transparent')
            }
            aria-label={t('common:memberClub.nav.closeMenuAriaLabel')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Fixed menu items. flex-1 + overflow-y-auto so a tenant with many
            quick actions scrolls the list instead of pushing the bottom
            section (language / admin / sign out) off the drawer. */}
        <nav className="mt-4 flex flex-1 flex-col gap-1 overflow-y-auto overscroll-contain px-3 pb-2">
          {FIXED_MENU_ITEMS.map((item) => {
            const active = pathname.startsWith(item.path);
            const Icon = item.icon;

            return (
              <Link
                key={item.path}
                href={item.path}
                onClick={onClose}
                className="flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors"
                style={{
                  color: drawerFg,
                  backgroundColor: active ? activeWash : 'transparent',
                }}
              >
                <Icon className="h-5 w-5 shrink-0 opacity-80" />
                <Trans i18nKey={item.labelKey} defaults={item.defaultLabel} />
              </Link>
            );
          })}

          {/* Dynamic items from home screen config */}
          {dynamicItems.length > 0 && (
            <>
              <div
                className="my-2"
                style={{ borderTop: `1px solid ${dividerWash}` }}
              />
              {dynamicItems.map((item) => {
                const active = pathname.startsWith(item.path);
                const Icon = item.icon;

                return (
                  <Link
                    key={item.path}
                    href={item.path}
                    onClick={onClose}
                    className="flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors"
                    style={{
                      color: drawerFg,
                      backgroundColor: active ? activeWash : 'transparent',
                    }}
                  >
                    <Icon className="h-5 w-5 shrink-0 opacity-80" />
                    {item.label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* Bottom section: language switcher + sign out */}
        <div
          className="mt-auto flex shrink-0 flex-col gap-1 px-3 pt-2"
          style={{
            // Clear the bottom tab bar (64px + its own safe-area padding),
            // which floats over the drawer's bottom edge. Padding alone used
            // to be safe-area-only, so Sign Out sat behind the tabs.
            paddingBottom: `calc(${SAFE_AREA_INSET.bottom} + ${hasTabRail ? 16 : 80}px)`,
            borderTop: `1px solid ${dividerWash}`,
          }}
        >
          {/* Language switcher */}
          <button
            onClick={toggleLanguage}
            className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors"
            style={{ color: drawerFg }}
          >
            <Globe className="h-5 w-5 shrink-0 opacity-80" />
            {i18n.language === 'ar' ? 'English' : 'العربية'}
          </button>

          {/* Admin panel — visible to authenticated users with a role on this tenant */}
          {isAuthenticated && hasTenantRole && (
            <Link
              href={adminPanelHref}
              onClick={onClose}
              className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors"
              style={{ color: drawerFg }}
            >
              <Shield className="h-5 w-5 shrink-0 opacity-80" />
              <Trans i18nKey="common:adminPanel" defaults="Admin Panel" />
            </Link>
          )}

          {/* Sign in / Sign out */}
          {isAuthenticated ? (
            <button
              onClick={() => {
                onClose();
                showSignOutLoader();
                clearBiometricCredentials();
                void signOut.mutateAsync();
              }}
              className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors"
              style={{ color: drawerFg }}
            >
              <LogOut className="h-5 w-5 shrink-0 opacity-80" />
              <Trans i18nKey="auth:signOut" defaults="Sign Out" />
            </button>
          ) : (
            <Link
              href="/auth/sign-in"
              onClick={onClose}
              className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors"
              style={{ color: drawerFg }}
            >
              <LogIn className="h-5 w-5 shrink-0 opacity-80" />
              <Trans i18nKey="auth:signIn" defaults="Sign In" />
            </Link>
          )}
        </div>
      </div>
    </>
  );

  // The route outlet sets `isolation: isolate`, so a drawer rendered inside the
  // page gets its own stacking context and paints UNDER the bottom tab bar —
  // which is portalled to a sibling host — no matter how high its z-index. That
  // hid Sign Out entirely and clipped Admin Panel. Portal to the overlay host,
  // declared after the tab bar host, so paint order puts the drawer on top.
  // Non-native (mobile web) has no host and renders inline, where z-[60] over
  // the tab bar's z-50 already works.
  return overlayContainer ? createPortal(drawer, overlayContainer) : drawer;
}
